import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Save, X, Calendar, Users, Settings, List, Eye, Edit, ChevronDown, ChevronUp, GripVertical, FileText, AlertCircle } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import Loader from "../../../common_components/Loader";
import { ALLIED_DEFAULT_SCHEMA, isCoreField } from "../../../modules/dynamic-form/constants/presets";
import { motion, AnimatePresence } from "framer-motion";
import { AdmissionVacancyStatus } from "../../../types/status";
import { DEFAULT_ALLIED_COURSES, getCourseDisplayName } from "../constants/courses";

// ---------- Types ----------

type SeatCount = { male: number; female: number };
type SeatsByCategory = Record<"UR" | "OBC" | "SC" | "ST" | "EWS", number>;

interface Course {
  name: string;
  seats: SeatsByCategory;
  femaleSeats?: number;
  pwdSeats?: number;
}

interface ApplicationDates {
  startDate: string;
  endDate: string;
}

type FeeCategory = "UR" | "OBC" | "SC" | "ST" | "EWS" | "PWD" | "FEMALE";

interface PaymentDetails {
  amount: Record<FeeCategory, number>;
  currency: string;
  modes: {
    online: { enabled: boolean; gatewayDetails?: { provider: string; merchantId: string } };
    offline: { enabled: boolean; instructions: string; requireUTR: boolean; requireScreenshot: boolean };
  };
}

interface FieldValidations {
  min?: number | "";
  max?: number | "";
  minLength?: number | "";
  maxLength?: number | "";
  regex?: string;
}

type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "email"
  | "phone"
  | "select"
  | "checkbox"
  | "address"
  | "file"
  | "image"
  | "repeater";

interface FormField {
  id: string;
  label: string;
  type: FieldType;
  required: boolean;
  editable?: boolean;
  options?: string[];
  validations?: FieldValidations;
  fields?: FormField[];
}

interface FormSection {
  id: string;
  title: string;
  description?: string;
  removable: boolean;
  fields: FormField[];
}

interface ApplicationFormSchema {
  sections: FormSection[];
}

interface VacancyFormData {
  _id?: string;
  title: string;
  admissionYear: number | "";
  status: AdmissionVacancyStatus;
  applicationDates: ApplicationDates;
  paymentDetails: PaymentDetails;
  courses: Course[];
  instructions: string[];
  applicationFormSchema: ApplicationFormSchema;
  admissionId?: string;
}

// What we receive from the backend / table listing. admissionId & _id present,
// applicationDates may come back as Date strings (or be partially missing on bad data).
interface VacancyRecord extends Omit<VacancyFormData, "applicationDates"> {
  _id: string;
  admissionId: string;
  applicationDates: {
    startDate: string;
    endDate: string;
  };
}

interface DraggedField {
  sectionId: string;
  index: number;
}

// ---------- Component ----------

const AdmissionVacancyManagement: React.FC = () => {
  const { apiRequest, isStudentAdmin } = useAuth();
  const [vacancies, setVacancies] = useState<VacancyRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"general" | "courses" | "formBuilder">("general");

  const getStatusColor = (status: AdmissionVacancyStatus | string) => {
    switch (status) {
      case AdmissionVacancyStatus.PUBLISHED:
        return "bg-green-100 text-green-800 border-green-200";
      case AdmissionVacancyStatus.DRAFT:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";

      case AdmissionVacancyStatus.ARCHIVED:
        return "bg-purple-100 text-purple-800 border-purple-200";
      case AdmissionVacancyStatus.DELETED:
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const getTotalSeats = (courses: Course[]) => {
    if (!courses || courses.length === 0) return 0;
    return courses.reduce((total, course) => {
      const seatSum = Object.values(course.seats || {}).reduce((sum, count) => sum + (count || 0), 0);
      return total + seatSum;
    }, 0);
  };

  const defaultFormState: VacancyFormData = {
    title: "",
    admissionYear: new Date().getFullYear(),
    status: AdmissionVacancyStatus.DRAFT,
    applicationDates: {
      startDate: new Date().toISOString().split("T")[0],
      endDate: new Date().toISOString().split("T")[0],
    },
    paymentDetails: {
      amount: { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0, PWD: 0, FEMALE: 0 },
      currency: "INR",
      modes: {
        online: { enabled: true, gatewayDetails: { provider: "", merchantId: "" } },
        offline: { enabled: true, instructions: "", requireUTR: true, requireScreenshot: true },
      },
    },
    courses:[],
    instructions: [""],
    applicationFormSchema: JSON.parse(JSON.stringify(ALLIED_DEFAULT_SCHEMA)),
  };

  const [formData, setFormData] = useState<VacancyFormData>(defaultFormState);

  // Pagination & Archive / Deleted state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showArchive, setShowArchive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchVacancies = async (pageNum = page, showArc = showArchive, showDel = showDeleted): Promise<void> => {
    setIsLoading(true);
    try {
      const url = showDel
        ? `/admission-vacancies?page=${pageNum}&limit=10&showDeleted=true`
        : `/admission-vacancies?page=${pageNum}&limit=10&showArchive=${showArc}`;
      const res = await apiRequest(url);
      setVacancies(res.data || []);
      setTotalPages(res.totalPages || 1);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch admission vacancies");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVacancies(page, showArchive, showDeleted);
  }, [page, showArchive, showDeleted]);

  const handleEdit = (vacancy: VacancyRecord): void => {
    const startDate = vacancy.applicationDates?.startDate
      ? new Date(vacancy.applicationDates.startDate).toISOString().split("T")[0]
      : defaultFormState.applicationDates.startDate;
    const endDate = vacancy.applicationDates?.endDate
      ? new Date(vacancy.applicationDates.endDate).toISOString().split("T")[0]
      : defaultFormState.applicationDates.endDate;

    setFormData({
      ...vacancy,
      applicationDates: { startDate, endDate },
      applicationFormSchema: vacancy.applicationFormSchema || JSON.parse(JSON.stringify(ALLIED_DEFAULT_SCHEMA)),
    });
    setIsEditing(true);
    setShowForm(true);
    setActiveTab("general");
  };

  // --- Dynamic Schema Builder Logic ---
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null); // sectionId.fieldId
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [draggedField, setDraggedField] = useState<DraggedField | null>(null);

  const handleLoadPreset = (): void => {
    setFormData({
      ...formData,
      applicationFormSchema: JSON.parse(JSON.stringify(ALLIED_DEFAULT_SCHEMA)),
    });
  };

  const toggleSectionCollapse = (sectionId: string): void => {
    setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const addCustomSection = (): void => {
    const sections = [...(formData.applicationFormSchema?.sections || [])];
    const newId = `section_${Date.now()}`;
    sections.push({
      id: newId,
      title: "New Custom Section",
      description: "",
      removable: true,
      fields: [],
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...(formData.applicationFormSchema || {}), sections },
    });
    setCollapsedSections((prev) => ({ ...prev, [newId]: false }));
  };

  const deleteSection = (sectionId: string): void => {
    const sections = formData.applicationFormSchema.sections.filter((s) => s.id !== sectionId);
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const updateSectionTitle = (sectionId: string, value: string): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) return { ...s, title: value };
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const updateSectionDesc = (sectionId: string, value: string): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) return { ...s, description: value };
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const addCustomField = (sectionId: string): void => {
    const newFieldId = `field_${Date.now()}`;
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields: FormField[] = s.fields ? [...s.fields] : [];
        fields.push({
          id: newFieldId,
          label: "New Custom Field",
          type: "text",
          required: false,
          editable: true,
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
    setEditingFieldId(`${sectionId}.${newFieldId}`);
  };

  const deleteField = (sectionId: string, fieldId: string): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields = s.fields.filter((f) => f.id !== fieldId);
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const updateFieldProperty = (
    sectionId: string,
    fieldId: string,
    propName: keyof FormField,
    value: FormField[keyof FormField]
  ): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f) => {
          if (f.id === fieldId) {
            const updated: FormField = { ...f, [propName]: value };
            if (propName === "type") {
              if (value !== "select") delete updated.options;
              if (value !== "repeater") delete updated.fields;
              if (value !== "number" && value !== "text") delete updated.validations;
            }
            return updated;
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const updateFieldValidation = (
    sectionId: string,
    fieldId: string,
    valProp: keyof FieldValidations,
    value: string | number | ""
  ): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f) => {
          if (f.id === fieldId) {
            const validations: FieldValidations = { ...(f.validations || {}), [valProp]: value };
            if (value === "" || value === undefined) {
              delete validations[valProp];
            }
            return { ...f, validations };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const handleAddSelectOption = (sectionId: string, fieldId: string, newOpt: string): void => {
    if (!newOpt.trim()) return;
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f) => {
          if (f.id === fieldId) {
            const options = [...(f.options || [])];
            if (!options.includes(newOpt)) options.push(newOpt);
            return { ...f, options };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const handleRemoveSelectOption = (sectionId: string, fieldId: string, optToRemove: string): void => {
    const sections = formData.applicationFormSchema.sections.map((s) => {
      if (s.id === sectionId) {
        const fields = s.fields.map((f) => {
          if (f.id === fieldId) {
            const options = (f.options || []).filter((o) => o !== optToRemove);
            return { ...f, options };
          }
          return f;
        });
        return { ...s, fields };
      }
      return s;
    });
    setFormData({
      ...formData,
      applicationFormSchema: { ...formData.applicationFormSchema, sections },
    });
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, sectionId: string, index: number): void => {
    setDraggedField({ sectionId, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handleDragEnd = (): void => {
    setDraggedField(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetSectionId: string, targetIndex: number): void => {
    e.preventDefault();
    if (!draggedField) return;

    const sourceSectionId = draggedField.sectionId;
    const sourceIndex = draggedField.index;

    if (sourceSectionId === targetSectionId && sourceIndex === targetIndex) {
      setDraggedField(null);
      return;
    }

    const sections: FormSection[] = JSON.parse(JSON.stringify(formData.applicationFormSchema.sections));
    const sourceSection = sections.find((s) => s.id === sourceSectionId);
    const targetSection = sections.find((s) => s.id === targetSectionId);

    if (!sourceSection || !targetSection) {
      setDraggedField(null);
      return;
    }

    const [movedField] = sourceSection.fields.splice(sourceIndex, 1);
    targetSection.fields.splice(targetIndex, 0, movedField);

    setFormData({
      ...formData,
      applicationFormSchema: {
        ...formData.applicationFormSchema,
        sections,
      },
    });

    setDraggedField(null);
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm("Are you sure you want to delete this vacancy?")) return;
    try {
      await apiRequest(`/admission-vacancies/${id}`, {
        method: "DELETE",
      });
      fetchVacancies(page, showArchive, showDeleted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete vacancy");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      if (isEditing) {
        await apiRequest(`/admission-vacancies/${formData._id}`, {
          method: "PUT",
          body: JSON.stringify(formData),
        });
      } else {
        await apiRequest("/admission-vacancies", {
          method: "POST",
          body: JSON.stringify(formData),
        });
      }
      setIsEditing(false);
      setShowForm(false);
      setFormData(defaultFormState);
      setActiveTab("general");
      fetchVacancies(page, showArchive, showDeleted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save vacancy");
    }
  };

  // Course Management Helpers
  const loadStandardCourses = (): void => {
    setFormData({
      ...formData,
      courses: DEFAULT_ALLIED_COURSES.map((c) => ({
        name: c.name,
        seats: { ...c.defaultSeats },
        femaleSeats: c.femaleSeats || 0,
        pwdSeats: c.pwdSeats || 0,
      })),
    });
  };

  const addPresetCourse = (courseName: string): void => {
    if (!courseName) return;
    const preset = DEFAULT_ALLIED_COURSES.find((c) => c.name === courseName || c.shortCode === courseName);
    const newCourse: Course = preset
      ? {
          name: preset.name,
          seats: { ...preset.defaultSeats },
          femaleSeats: preset.femaleSeats || 0,
          pwdSeats: preset.pwdSeats || 0,
        }
      : {
          name: courseName,
          seats: { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 },
          femaleSeats: 0,
          pwdSeats: 0,
        };

    setFormData({
      ...formData,
      courses: [...formData.courses, newCourse],
    });
  };

  const moveCourse = (index: number, direction: "up" | "down"): void => {
    const newCourses = [...formData.courses];
    const targetIdx = direction === "up" ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newCourses.length) return;
    const temp = newCourses[index];
    newCourses[index] = newCourses[targetIdx];
    newCourses[targetIdx] = temp;
    setFormData({ ...formData, courses: newCourses });
  };

  const addCourse = (): void => {
    setFormData({
      ...formData,
      courses: [
        ...formData.courses,
        {
          name: "",
          seats: {
            UR: 0,
            OBC: 0,
            SC: 0,
            ST: 0,
            EWS: 0,
          },
          femaleSeats: 0,
          pwdSeats: 0,
        },
      ],
    });
  };

  const updateCourse = (
    index: number,
    field: "name" | "femaleSeats" | "pwdSeats",
    value: string,
    category?: keyof SeatsByCategory
  ): void => {
    const newCourses = [...formData.courses];
    if (category) {
      newCourses[index].seats[category] = parseInt(value, 10) || 0;
    } else if (field === "femaleSeats" || field === "pwdSeats") {
      newCourses[index][field] = parseInt(value, 10) || 0;
    } else {
      newCourses[index][field] = value;
    }
    setFormData({ ...formData, courses: newCourses });
  };

  const updateFee = (category: FeeCategory, value: string): void => {
    setFormData({
      ...formData,
      paymentDetails: {
        ...formData.paymentDetails,
        amount: {
          ...formData.paymentDetails.amount,
          [category]: parseInt(value, 10) || 0,
        },
      },
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-slate-900 mb-2 tracking-tight leading-tight">
              Admission Vacancies
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
              Manage all Allied Health Sciences admission drives, course seat matrices, and application form schemas
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isStudentAdmin && (
              <label className="inline-flex items-center cursor-pointer gap-2 bg-red-50 hover:bg-red-100 px-4 py-3 rounded border border-red-200 shadow-sm transition-colors">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    if (e.target.checked) setShowArchive(false);
                    setPage(1);
                  }}
                  className="rounded text-red-600 focus:ring-red-500 border-red-300 w-4 h-4 cursor-pointer"
                />
                <span className="text-sm font-semibold text-red-700">Show Deleted Items</span>
              </label>
            )}
            <label className="inline-flex items-center cursor-pointer gap-2 bg-white hover:bg-slate-100 px-4 py-3 rounded border border-slate-200 shadow-sm transition-colors">
              <input
                type="checkbox"
                checked={showArchive}
                onChange={(e) => {
                  setShowArchive(e.target.checked);
                  if (e.target.checked) setShowDeleted(false);
                  setPage(1);
                }}
                className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
              />
              <span className="text-sm font-semibold text-slate-700">Show Archive</span>
            </label>
            <button
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                  setIsEditing(false);
                  setFormData(defaultFormState);
                } else {
                  setFormData(defaultFormState);
                  setIsEditing(false);
                  setShowForm(true);
                }
              }}
              className="flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded border border-slate-700 shadow-sm font-medium hover:bg-slate-900 transition-all"
            >
              {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {showForm ? "Close Form" : "Create Admission Vacancy"}
            </button>
          </div>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">
                Error Loading Vacancies
              </h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </motion.div>
        )}

        {showForm && (
          <div className="bg-white p-6 rounded-xl shadow-md mb-8 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">
              {isEditing ? "Edit Vacancy" : "Create New Admission Vacancy"}
            </h3>

        {/* Tab Headers */}
        <div className="flex border-b border-slate-200 mb-6 bg-slate-50 p-1.5 rounded-lg gap-2">
          {(
            [
              { id: "general", label: "General & Fees", icon: Calendar },
              { id: "courses", label: "Courses & Seats", icon: List },
              { id: "formBuilder", label: "Application Form Builder", icon: Settings },
            ] as const
          ).map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                  isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:text-blue-600 hover:bg-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <AnimatePresence mode="wait">
            {activeTab === "general" && (
              <motion.div
                key="general"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Vacancy Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                      placeholder="e.g. Allied Health Sciences Admissions 2025"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Admission Year *</label>
                    <input
                      type="number"
                      value={formData.admissionYear}
                      onChange={(e) =>
                        setFormData({ ...formData, admissionYear: e.target.value === "" ? "" : parseInt(e.target.value, 10) || "" })
                      }
                      required
                      className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value as AdmissionVacancyStatus })
                      }
                      className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value={AdmissionVacancyStatus.DRAFT}>{AdmissionVacancyStatus.DRAFT}</option>
                      <option value={AdmissionVacancyStatus.PUBLISHED}>{AdmissionVacancyStatus.PUBLISHED}</option>
                      <option value={AdmissionVacancyStatus.DELETED}>{AdmissionVacancyStatus.DELETED}</option>
                      <option value={AdmissionVacancyStatus.ARCHIVED}>{AdmissionVacancyStatus.ARCHIVED}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={formData.applicationDates.startDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          applicationDates: { ...formData.applicationDates, startDate: e.target.value },
                        })
                      }
                      required
                      className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">End Date *</label>
                    <input
                      type="date"
                      value={formData.applicationDates.endDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          applicationDates: { ...formData.applicationDates, endDate: e.target.value },
                        })
                      }
                      required
                      className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-lg font-bold text-slate-800 mb-3">Application Fees (INR)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {(["UR", "OBC", "SC", "ST", "EWS", "PWD", "FEMALE"] as FeeCategory[]).map((cat) => (
                      <div key={cat} className="space-y-1">
                        <label className="block text-xs font-semibold text-slate-650 mb-1">{cat} Fee</label>
                        <input
                          type="number"
                          value={formData.paymentDetails.amount[cat]}
                          onChange={(e) => updateFee(cat, e.target.value)}
                          className="w-full border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-center bg-white"
                          min="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "courses" && (
              <motion.div
                key="courses"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                className="space-y-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b pb-4 gap-3">
                  <div>
                    <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      Courses & Seat Matrices
                      <span className="text-xs bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full font-semibold">
                        {formData.courses.length} Courses
                      </span>
                      <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full font-semibold">
                        {getTotalSeats(formData.courses)} Total Seats
                      </span>
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">Configure admission disciplines, seat distribution, and reservation matrices.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={loadStandardCourses}
                      className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 border border-slate-300 text-xs font-semibold transition-colors flex items-center gap-1"
                    >
                      <span>Load 5 Standard Courses</span>
                    </button>

                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          addPresetCourse(e.target.value);
                          e.target.value = "";
                        }
                      }}
                      className="px-3 py-1.5 bg-white text-slate-700 rounded-lg border border-slate-300 text-xs font-semibold focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>+ Add Standard Course...</option>
                      {DEFAULT_ALLIED_COURSES.map((c) => (
                        <option key={c.shortCode} value={c.name}>
                          {c.shortCode} ({c.name})
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={addCourse}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-semibold transition-colors shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Blank Course
                    </button>
                  </div>
                </div>

                {formData.courses.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300 space-y-3">
                    <p className="text-slate-500 text-sm">No courses configured yet. Click below to load standard courses.</p>
                    <button
                      type="button"
                      onClick={loadStandardCourses}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-bold transition-colors shadow-sm"
                    >
                      Load 5 Standard AIIMS Courses
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.courses.map((course, idx) => (
                      <div key={idx} className="bg-slate-50 border border-slate-200 p-5 rounded-xl space-y-4 relative hover:border-slate-300 transition-all">
                        <div className="flex items-center justify-between border-b pb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-600 bg-white border px-2 py-0.5 rounded">
                              Course #{idx + 1}
                            </span>
                            <span className="text-xs font-bold text-blue-900">
                              {getCourseDisplayName(course.name)}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => moveCourse(idx, "up")}
                              title="Move Up"
                              className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:pointer-events-none rounded hover:bg-white"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              disabled={idx === formData.courses.length - 1}
                              onClick={() => moveCourse(idx, "down")}
                              title="Move Down"
                              className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:pointer-events-none rounded hover:bg-white"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            {formData.courses.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newCourses = formData.courses.filter((_, i) => i !== idx);
                                  setFormData({ ...formData, courses: newCourses });
                                }}
                                className="text-xs flex items-center gap-1 text-red-650 hover:bg-red-50 px-2 py-1 rounded border border-red-150 transition-colors ml-2"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="md:col-span-2 space-y-1">
                            <label className="block text-xs font-bold text-slate-650 mb-1">Course Title / Name *</label>
                            <input
                              type="text"
                              value={course.name}
                              onChange={(e) => updateCourse(idx, "name", e.target.value)}
                              placeholder="Course Name (e.g. B.Sc. EMT)"
                              required
                              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white font-medium"
                            />
                            {/* Preset suggestion pill buttons */}
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              <span className="text-[10px] text-slate-400 self-center">Presets:</span>
                              {DEFAULT_ALLIED_COURSES.map((preset) => (
                                <button
                                  key={preset.shortCode}
                                  type="button"
                                  onClick={() => updateCourse(idx, "name", preset.name)}
                                  className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                                    course.name === preset.name
                                      ? "bg-blue-100 text-blue-800 border-blue-300 font-bold"
                                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                                  }`}
                                >
                                  {preset.shortCode}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 pt-2">
                          <label className="block text-xs font-bold text-slate-500">Seat Matrix by Category</label>
                          <div className="grid grid-cols-5 gap-3">
                            {(["UR", "OBC", "SC", "ST", "EWS"] as (keyof SeatsByCategory)[]).map((cat) => (
                              <div key={cat} className="border border-slate-200 p-2.5 rounded-lg bg-white space-y-1">
                                <label className="block text-xs font-bold text-center border-b pb-1 text-slate-700">{cat}</label>
                                <input
                                  type="number"
                                  value={course.seats[cat] ?? 0}
                                  onChange={(e) => updateCourse(idx, "name", e.target.value, cat)}
                                  className="w-full border border-slate-300 rounded p-1 text-center text-xs font-bold"
                                  min="0"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div className="space-y-1">
                            <label className="block text-xs font-bold text-slate-650">Reserved for Female (Horizontal)</label>
                            <input
                              type="number"
                              value={course.femaleSeats ?? 0}
                              onChange={(e) => updateCourse(idx, "femaleSeats", e.target.value)}
                              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                              min="0"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="block text-xs font-bold text-slate-650">Reserved for PwD (Horizontal)</label>
                            <input
                              type="number"
                              value={course.pwdSeats ?? 0}
                              onChange={(e) => updateCourse(idx, "pwdSeats", e.target.value)}
                              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === "formBuilder" && (
              <motion.div
                key="formBuilder"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                className="space-y-6"
              >
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm">
                  <div>
                    <h4 className="text-lg font-bold text-slate-800">Application Form Designer</h4>
                    <p className="text-xs text-slate-500 mt-1">Configure sections, field validations, and custom fields for this vacancy.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleLoadPreset}
                      className="px-3.5 py-2 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                    >
                      Reset Form to Default Preset
                    </button>
                    <button
                      type="button"
                      onClick={addCustomSection}
                      className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
                    >
                      <Plus className="w-4 h-4" /> Add Section
                    </button>
                  </div>
                </div>

                {/* Sections Accordion */}
                <div className="space-y-4">
                  {(formData.applicationFormSchema?.sections || []).map((section, sIndex) => {
                    const isCollapsed = collapsedSections[section.id] !== false;
                    return (
                      <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        {/* Section Header */}
                        <div
                          className="bg-slate-50 p-4 border-b border-slate-200 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => toggleSectionCollapse(section.id)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="font-bold text-slate-800 text-sm">
                              Section #{sIndex + 1}: {section.title || "Untitled Section"}
                            </span>
                            {!section.removable && (
                              <span className="px-2 py-0.5 text-[9px] font-bold text-slate-500 bg-slate-200 rounded uppercase tracking-wider">
                                Required Core
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            {section.removable && (
                              <button
                                type="button"
                                onClick={() => deleteSection(section.id)}
                                className="p-1.5 text-red-650 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-150"
                                title="Delete Section"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleSectionCollapse(section.id)}
                              className="p-1.5 text-slate-500 hover:bg-slate-205 rounded-lg transition-colors"
                            >
                              {isCollapsed ? <ChevronDown className="w-4.5 h-4.5" /> : <ChevronUp className="w-4.5 h-4.5" />}
                            </button>
                          </div>
                        </div>

                        {/* Section Body */}
                        <AnimatePresence initial={false}>
                          {!isCollapsed && (
                            <motion.div
                              initial={{ height: 0 }}
                              animate={{ height: "auto" }}
                              exit={{ height: 0 }}
                              className="p-5 border-t border-slate-100 space-y-5 overflow-hidden"
                            >
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                  <label className="block text-xs font-bold text-slate-600">Section Title</label>
                                  <input
                                    type="text"
                                    value={section.title}
                                    onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                                    placeholder="e.g. Personal Details"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white font-medium"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="block text-xs font-bold text-slate-600">Description (Optional)</label>
                                  <input
                                    type="text"
                                    value={section.description || ""}
                                    onChange={(e) => updateSectionDesc(section.id, e.target.value)}
                                    placeholder="Provide optional guidance text for applicant"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white font-medium"
                                  />
                                </div>
                              </div>

                              {/* Section Fields list */}
                              <div className="space-y-4 pt-4 border-t border-slate-100">
                                <div className="flex items-center justify-between">
                                  <h5 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Form Fields</h5>
                                  <button
                                    type="button"
                                    onClick={() => addCustomField(section.id)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-650 hover:bg-blue-100 rounded-lg border border-blue-100 text-xs font-bold transition-colors"
                                  >
                                    <Plus className="w-3.5 h-3.5" /> Add Field
                                  </button>
                                </div>

                                {!section.fields || section.fields.length === 0 ? (
                                  <div
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, section.id, 0)}
                                    className="text-center py-6 border border-dashed border-slate-300 rounded-xl bg-slate-50 transition-colors hover:bg-blue-50/30 hover:border-blue-300"
                                  >
                                    <span className="text-xs text-slate-400 font-medium">No fields in this section. Drag a field here.</span>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    {section.fields.map((field, fieldIndex) => {
                                      const isLocked = isCoreField(field.id);
                                      const isFieldEditing = editingFieldId === `${section.id}.${field.id}`;
                                      return (
                                        <div
                                          key={field.id}
                                          draggable={!isFieldEditing}
                                          onDragStart={(e) => handleDragStart(e, section.id, fieldIndex)}
                                          onDragOver={handleDragOver}
                                          onDrop={(e) => handleDrop(e, section.id, fieldIndex)}
                                          onDragEnd={handleDragEnd}
                                          className={`p-3.5 border rounded-lg bg-slate-50 border-slate-200 space-y-3 transition-all ${
                                            draggedField && draggedField.sectionId === section.id && draggedField.index === fieldIndex
                                              ? "opacity-40 border-dashed border-blue-400 bg-blue-50/30 scale-[0.98] cursor-grabbing"
                                              : "hover:border-slate-300 hover:bg-slate-100/50 cursor-grab"
                                          }`}
                                        >
                                          <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                              <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-650 transition-colors mr-1" />
                                              <span className="font-bold text-xs text-slate-800">{field.label || "Unnamed Field"}</span>
                                              <span className="px-2 py-0.5 text-[9px] font-bold text-slate-550 bg-white border border-slate-200 rounded uppercase">
                                                {field.type}
                                              </span>
                                              {field.required && (
                                                <span className="px-1.5 py-0.5 text-[9px] font-bold text-red-650 bg-red-50 border border-red-100 rounded">
                                                  Required
                                                </span>
                                              )}
                                              {isLocked && (
                                                <span className="px-2 py-0.5 text-[9px] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded">
                                                  Locked Core
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                              <button
                                                type="button"
                                                onClick={() => setEditingFieldId(isFieldEditing ? null : `${section.id}.${field.id}`)}
                                                className="p-1 text-slate-650 hover:bg-slate-200 rounded-md transition-colors"
                                                title="Edit properties"
                                              >
                                                {isFieldEditing ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                                              </button>
                                              {!isLocked && (
                                                <button
                                                  type="button"
                                                  onClick={() => deleteField(section.id, field.id)}
                                                  className="p-1 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                  title="Delete field"
                                                >
                                                  <Trash2 className="w-4 h-4" />
                                                </button>
                                              )}
                                            </div>
                                          </div>

                                          {isFieldEditing && (
                                            <div className="p-4 bg-white rounded-lg border border-slate-200 space-y-4">
                                              <div className="grid md:grid-cols-3 gap-4">
                                                <div className="space-y-1">
                                                  <label className="block text-[11px] font-bold text-slate-600">Field Label</label>
                                                  <input
                                                    type="text"
                                                    value={field.label}
                                                    onChange={(e) => updateFieldProperty(section.id, field.id, "label", e.target.value)}
                                                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold bg-white"
                                                  />
                                                </div>

                                                <div className="space-y-1">
                                                  <label className="block text-[11px] font-bold text-slate-600">Field Type</label>
                                                  <select
                                                    value={field.type}
                                                    disabled={isLocked}
                                                    onChange={(e) =>
                                                      updateFieldProperty(section.id, field.id, "type", e.target.value as FieldType)
                                                    }
                                                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold bg-white disabled:bg-slate-100 disabled:text-slate-500"
                                                  >
                                                    <option value="text">Single Line Text</option>
                                                    <option value="textarea">Multi-line Text (Textarea)</option>
                                                    <option value="number">Number</option>
                                                    <option value="date">Date</option>
                                                    <option value="email">Email Address</option>
                                                    <option value="phone">Mobile Phone</option>
                                                    <option value="select">Dropdown Select</option>
                                                    <option value="checkbox">Checkbox Toggle</option>
                                                    <option value="address">Sub-Address block</option>
                                                    <option value="file">PDF File Upload</option>
                                                    <option value="image">Image File Upload</option>
                                                    <option value="repeater">Repeater Rows list</option>
                                                  </select>
                                                </div>

                                                <div className="space-y-4 pt-6">
                                                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                                    <input
                                                      type="checkbox"
                                                      checked={!!field.required}
                                                      disabled={isLocked && field.required}
                                                      onChange={(e) => updateFieldProperty(section.id, field.id, "required", e.target.checked)}
                                                      className="w-3.5 h-3.5 text-blue-600 rounded border-slate-300"
                                                    />
                                                    <span className="text-[11px] font-bold text-slate-650">Required Field</span>
                                                  </label>
                                                </div>
                                              </div>

                                              {/* Dropdown Options List */}
                                              {field.type === "select" && (
                                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                                                  <span className="block text-xs font-bold text-slate-700">Dropdown Options</span>
                                                  <div className="flex gap-2">
                                                    <input
                                                      type="text"
                                                      id={`new-opt-${section.id}-${field.id}`}
                                                      placeholder="Add option text"
                                                      className="px-2.5 py-1 border border-slate-300 rounded text-xs flex-1 bg-white font-medium"
                                                      onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                          e.preventDefault();
                                                          const target = e.target as HTMLInputElement;
                                                          handleAddSelectOption(section.id, field.id, target.value);
                                                          target.value = "";
                                                        }
                                                      }}
                                                    />
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        const el = document.getElementById(
                                                          `new-opt-${section.id}-${field.id}`
                                                        ) as HTMLInputElement | null;
                                                        if (!el) return;
                                                        handleAddSelectOption(section.id, field.id, el.value);
                                                        el.value = "";
                                                      }}
                                                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-bold transition-all"
                                                    >
                                                      Add
                                                    </button>
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                                    {(field.options || []).map((opt) => (
                                                      <span key={opt} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-white border border-slate-200 text-xs text-slate-650 font-semibold shadow-sm">
                                                        {opt}
                                                        <button
                                                          type="button"
                                                          onClick={() => handleRemoveSelectOption(section.id, field.id, opt)}
                                                          className="text-red-500 hover:text-red-750 font-bold ml-1 text-sm leading-none"
                                                        >
                                                          &times;
                                                        </button>
                                                      </span>
                                                    ))}
                                                    {(field.options || []).length === 0 && (
                                                      <span className="text-xs text-amber-600 font-bold">Add at least one selection option.</span>
                                                    )}
                                                  </div>
                                                </div>
                                              )}

                                              {/* Numeric range validations */}
                                              {field.type === "number" && (
                                                <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                                  <div className="space-y-1">
                                                    <label className="block text-[10px] font-bold text-slate-700">Minimum Value</label>
                                                    <input
                                                      type="number"
                                                      value={field.validations?.min !== undefined ? field.validations.min : ""}
                                                      onChange={(e) =>
                                                        updateFieldValidation(
                                                          section.id,
                                                          field.id,
                                                          "min",
                                                          e.target.value === "" ? "" : Number(e.target.value)
                                                        )
                                                      }
                                                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white font-semibold"
                                                      placeholder="None"
                                                    />
                                                  </div>
                                                  <div className="space-y-1">
                                                    <label className="block text-[10px] font-bold text-slate-700">Maximum Value</label>
                                                    <input
                                                      type="number"
                                                      value={field.validations?.max !== undefined ? field.validations.max : ""}
                                                      onChange={(e) =>
                                                        updateFieldValidation(
                                                          section.id,
                                                          field.id,
                                                          "max",
                                                          e.target.value === "" ? "" : Number(e.target.value)
                                                        )
                                                      }
                                                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white font-semibold"
                                                      placeholder="None"
                                                    />
                                                  </div>
                                                </div>
                                              )}

                                              {/* Text constraints validations */}
                                              {(field.type === "text" || field.type === "textarea") && (
                                                <div className="grid grid-cols-3 gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                                  <div className="space-y-1">
                                                    <label className="block text-[10px] font-bold text-slate-700">Min. Length</label>
                                                    <input
                                                      type="number"
                                                      value={field.validations?.minLength !== undefined ? field.validations.minLength : ""}
                                                      onChange={(e) =>
                                                        updateFieldValidation(
                                                          section.id,
                                                          field.id,
                                                          "minLength",
                                                          e.target.value === "" ? "" : Number(e.target.value)
                                                        )
                                                      }
                                                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white font-semibold"
                                                      placeholder="None"
                                                    />
                                                  </div>
                                                  <div className="space-y-1">
                                                    <label className="block text-[10px] font-bold text-slate-700">Max. Length</label>
                                                    <input
                                                      type="number"
                                                      value={field.validations?.maxLength !== undefined ? field.validations.maxLength : ""}
                                                      onChange={(e) =>
                                                        updateFieldValidation(
                                                          section.id,
                                                          field.id,
                                                          "maxLength",
                                                          e.target.value === "" ? "" : Number(e.target.value)
                                                        )
                                                      }
                                                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white font-semibold"
                                                      placeholder="None"
                                                    />
                                                  </div>
                                                  <div className="space-y-1">
                                                    <label className="block text-[10px] font-bold text-slate-700">Regex Pattern</label>
                                                    <input
                                                      type="text"
                                                      value={field.validations?.regex || ""}
                                                      onChange={(e) => updateFieldValidation(section.id, field.id, "regex", e.target.value)}
                                                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono bg-white font-semibold"
                                                      placeholder="e.g. ^\d{10}$"
                                                    />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="submit"
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 flex items-center gap-2 font-bold transition-all text-sm"
            >
              <Save className="w-4 h-4" /> {isEditing ? "Update Vacancy" : "Create Vacancy"}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(defaultFormState);
                  setActiveTab("general");
                }}
                className="px-6 py-2.5 bg-slate-200 text-slate-700 rounded-lg shadow-sm hover:bg-slate-300 flex items-center gap-2 font-bold transition-all text-sm"
              >
                <X className="w-4 h-4" /> Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>
      )}

        {isLoading && <Loader text="Loading admission vacancies..." />}

        {!isLoading && vacancies.length === 0 && !error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xs border border-slate-200 p-12 text-center"
          >
            <FileText className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2">
              No Vacancies Found
            </h2>
            <p className="text-slate-600 mb-6">
              Get started by creating your first Allied Health Sciences admission vacancy
            </p>
            <button
              onClick={() => {
                setFormData(defaultFormState);
                setIsEditing(false);
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded border border-slate-700 shadow-sm font-medium hover:bg-slate-900 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create First Vacancy
            </button>
          </motion.div>
        )}

        {!isLoading && vacancies.length > 0 && (
          <div className="grid gap-6">
            {vacancies.map((vacancy, index) => (
              <motion.div
                key={vacancy._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-xs border border-slate-200 hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <h2 className="text-2xl font-serif font-bold text-slate-900">
                        {vacancy.title}
                      </h2>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                          vacancy.status
                        )}`}
                      >
                        {vacancy.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 font-mono">
                      ID: {vacancy.admissionId} | Admission Year: {vacancy.admissionYear}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-slate-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100/70 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Start Date</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {vacancy.applicationDates?.startDate
                          ? new Date(vacancy.applicationDates.startDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100/70 rounded-lg">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">End Date</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {vacancy.applicationDates?.endDate
                          ? new Date(vacancy.applicationDates.endDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100/70 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Total Courses</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {vacancy.courses?.length || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100/70 rounded-lg">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Total Seats</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {getTotalSeats(vacancy.courses)}
                      </p>
                    </div>
                  </div>
                </div>

                {vacancy.courses && vacancy.courses.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Offered Courses:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {vacancy.courses.map((course, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200"
                        >
                          {course.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                  {vacancy.status !== AdmissionVacancyStatus.ARCHIVED &&
                    vacancy.status !== AdmissionVacancyStatus.DELETED && (
                      <button
                        onClick={() => handleEdit(vacancy)}
                        className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-xs hover:bg-slate-200 transition-colors font-medium text-sm"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}

                  <Link
                    to={`/allied/admin/${vacancy.admissionId}/applications/all`}
                    className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-xs hover:bg-slate-200 transition-colors font-medium text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    See Applications
                  </Link>

                  {vacancy.status !== AdmissionVacancyStatus.ARCHIVED &&
                    vacancy.status !== AdmissionVacancyStatus.DELETED && (
                      <button
                        onClick={() => handleDelete(vacancy._id)}
                        className="flex items-center gap-2 px-4 py-2 text-red-700 bg-red-50 border border-red-200 rounded shadow-xs hover:bg-red-100 transition-colors font-medium text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}

                  {vacancy.status === AdmissionVacancyStatus.DELETED && (
                    <span className="px-3 py-1.5 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded">
                      DELETED
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-6">
          <p className="text-xs text-slate-500 font-medium">
            Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
            <span className="font-semibold text-slate-800">{totalPages}</span>
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Previous
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default AdmissionVacancyManagement;